/**
 * @file App.js
 * @description Root application component setting up React Router routes, authentication guards, and context providers.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import { HelmetProvider } from 'react-helmet-async';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerProfile from './pages/CustomerProfile';
import AddCustomer from './pages/AddCustomer';
import Cheques from './pages/Cheques';
import ChequeDetail from './pages/ChequeDetail';
import CashCheck from './pages/CashCheck';
import DemoCashCheck from './pages/DemoCashCheck';
import BlockedCustomers from './pages/BlockedCustomers';
import BlockedCompanies from './pages/BlockedCompanies';
import Banks from './pages/Banks';
import Companies from './pages/Companies';
import Reports from './pages/Reports';
import DualCamera from './pages/DualCamera';
import CashCheckNew from './pages/CashCheckNew';
import SuperAdminDashboard from './pages/SuperAdminDashboard';

import './index.css';

/**
 * Higher-order route wrapper restricting access to authenticated users.
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child component to render if authorized.
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--dark)' }}>
      <div style={{ textAlign: 'center', color: 'white' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 900, marginBottom: 16 }}>
          Secure<span style={{ color: 'var(--accent)' }}>Check</span>
        </div>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
};

/**
 * Route wrapper restricting access exclusively to Super Admin users.
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child component.
 */
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

/**
 * Route wrapper restricting access for authenticated users (redirecting to dashboard).
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Public page component.
 */
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? <Navigate to="/dashboard" replace /> : children;
};

/**
 * Component defining all router route definitions.
 * @component
 */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/customers/new" element={<ProtectedRoute><AddCustomer /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerProfile /></ProtectedRoute>} />
      <Route path="/cheques" element={<ProtectedRoute><Cheques /></ProtectedRoute>} />
      <Route path="/cheques/new" element={<ProtectedRoute><CashCheck /></ProtectedRoute>} />
      <Route path="/cheques/demo" element={<ProtectedRoute><DemoCashCheck /></ProtectedRoute>} />
      <Route path="/cheques/:id" element={<ProtectedRoute><ChequeDetail /></ProtectedRoute>} />
      <Route path="/blocked-customers" element={<ProtectedRoute><BlockedCustomers /></ProtectedRoute>} />
      <Route path="/blocked-companies" element={<ProtectedRoute><BlockedCompanies /></ProtectedRoute>} />
      <Route path="/banks"             element={<ProtectedRoute><Banks /></ProtectedRoute>} />
      <Route path="/companies"         element={<ProtectedRoute><Companies /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/dual-camera" element={<ProtectedRoute><DualCamera /></ProtectedRoute>} />
      <Route path="/cheques/new2" element={<ProtectedRoute><CashCheckNew /></ProtectedRoute>} />
      <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/**
 * Root App component wrapping route definitions with global providers.
 * @component
 * @returns {React.ReactElement}
 */
export default function App() {
  return (
    <StyleSheetManager shouldForwardProp={isPropValid}>
      <AuthProvider>
        <TenantProvider>
          <HelmetProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Toaster
              position="top-right"
              gutter={8}
              containerStyle={{ top: 72 }}
              toastOptions={{
                duration: 4000,
                style: {
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 13.5,
                  borderRadius: 10,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.14)',
                  padding: '10px 14px',
                  maxWidth: 380,
                },
                success: {
                  duration: 3500,
                  iconTheme: { primary: '#1a7a4a', secondary: '#fff' },
                  style: { background: '#f0fdf4', color: '#14532d', border: '1px solid #bbf7d0' },
                },
                error: {
                  duration: 5000,
                  iconTheme: { primary: '#e03c3c', secondary: '#fff' },
                  style: { background: '#fef2f2', color: '#7f1d1d', border: '1px solid #fecaca' },
                },
                loading: {
                  style: { background: '#fff', color: '#1c2b38', border: '1px solid #d1d9e0' },
                },
              }}
            />
            <AppRoutes />
          </BrowserRouter>
        </HelmetProvider>
      </TenantProvider>
    </AuthProvider>
    </StyleSheetManager>
  );
}
